import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'clsx';

import { CACHE } from '../common/constants/playlists';
import { CHANNEL_LIST_PATH } from '../common/constants/routePath';
import {
  DESIGN_BACKGROUND,
  DESIGN_INLINE,
  DESIGN_INLINE_VERTICAL,
  DESIGN_STANDARD,
  DESIGN_VERTICAL,
} from './constants/carousels';

import { useSelectedChipFromQuery } from '../common/components/Chips/helpers/useSelectedChipFromQuery';
import { scrollToPublisherAnchor } from '../common/helpers/dom';
import { filterVideosByChip } from '../common/helpers/video';
import * as commonSelectors from '../common/redux/selectors/common';
import { channelsFetchStartAction } from '../common/redux/slices/common';
import { isMobileUserAgent } from '@/modules/common/helpers/userAgent';

import Chips from '@/modules/common/components/Chips/Chips';
import ChipsSkeleton from '../common/components/Chips/ChipsSkeleton';
import ScrollObserver from '../common/components/ScrollObserver/ScrollObserver';
import BackgroundCarousel from './components/BackgroundCarousel/BackgroundCarousel';
import DefaultCarousel from './components/DefaultCarousel/DefaultCarousel';
import Header from './components/Header/Header';
import InlineCarousel from './components/InlineCarousel/InlineCarousel';
import InlineVerticalCarousel from './components/InlineVerticalCarousel/InlineVerticalCarousel';
import VerticalCarousel from './components/VerticalCarousel/VerticalCarousel';

import styles from './ChannelList.module.scss';

function ChannelListPage() {
  const dispatch = useDispatch();
  const selectedChip = useSelectedChipFromQuery();
  const search = useSelector(commonSelectors.searchSelector);
  const channels = useSelector(commonSelectors.channelsSelector);
  const tagTypes = useSelector(commonSelectors.tagTypesSelector);
  const tagPages = useSelector(commonSelectors.tagPagesSelector);

  const carouselCollection = {
    [DESIGN_STANDARD]: DefaultCarousel,
    [DESIGN_BACKGROUND]: BackgroundCarousel,
    [DESIGN_INLINE]: InlineCarousel,
    [DESIGN_VERTICAL]: VerticalCarousel,
    [DESIGN_INLINE_VERTICAL]: InlineVerticalCarousel,
  };

  useEffect(() => {
    scrollToPublisherAnchor();

    const unknownChannels = channels.filter((chl) => !chl.playlistLoaded && !chl.isDisabled);

    if (unknownChannels.length) {
      dispatch(
        channelsFetchStartAction({
          cacheUsage: CACHE,
          channels: unknownChannels,
        }),
      );
    }
  }, []);

  const channelsToShow = channels.filter((channel) => !channel.isDisabled);

  const filteredChannelsToShow =
    tagPages.includes(CHANNEL_LIST_PATH) && selectedChip
      ? channelsToShow
          .map((channel) => {
            const playlist = filterVideosByChip(channel.playlist, tagTypes, selectedChip);

            return playlist.length ? { ...channel, playlist } : null;
          })
          .filter(Boolean)
      : channelsToShow;

  return (
    <div
      data-a="page-channel-list"
      className={classNames(styles.Wrapper, {
        [styles.Wrapper___withoutSearch]: !search,
      })}
    >
      {!isMobileUserAgent && search && <Header />}
      {tagPages.includes(CHANNEL_LIST_PATH) && (
        <>
          {!!channelsToShow.length && !channelsToShow.some((channel) => channel.playlistLoaded) && (
            <ChipsSkeleton className={styles.ChipWrapper} />
          )}
          <Chips videos={channelsToShow.flatMap((channel) => channel.playlist)} className={styles.ChipWrapper} />
        </>
      )}
      <div aria-label="Videos by channels">
        {filteredChannelsToShow.map((channel) => {
          const CarouselComponent = carouselCollection[channel.design];

          return (
            <CarouselComponent
              key={channel.name}
              titleColor={channel.titleColor}
              descColor={channel.descColor}
              vidTitleColor={channel.vidTitleColor}
              vidInfoColor={channel.vidInfoColor}
              bg={channel.bg}
              bgLogo={channel.bgLogo}
              channel={channel}
            />
          );
        })}
      </div>
      <ScrollObserver />
    </div>
  );
}

export default ChannelListPage;
